if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    /**
     * PDP 主区域加购成功埋点（配置见 main-product 内 `<script class="js-pdp-ga-atc-json">`，避免 data 属性破坏标签）
     * 必须永不抛错、不得阻塞加购 UI（抽屉/跳转）。
     * @param {object} data - /cart/add 返回 JSON
     * @param {FormData} formData - 表单数据
     */
    trackPdpGaAddToCartSuccess(data, formData) {
      try {
        const cfgEl = this.querySelector('script.js-pdp-ga-atc-json');
        if (!cfgEl || !cfgEl.textContent.trim()) return;

        let cfg;
        try {
          cfg = JSON.parse(cfgEl.textContent);
        } catch (parseErr) {
          return;
        }
        if (!cfg || !cfg.prefix) return;

        const variantId = data.variant_id
          || (Array.isArray(data.items) && data.items[0] && data.items[0].variant_id);
        const qtyRaw = formData.get('quantity');
        const quantity = qtyRaw ? parseInt(qtyRaw, 10) || 1 : 1;
        const deviceType =
          typeof theme !== 'undefined' && theme.mediaMatches && theme.mediaMatches.md
            ? 'desktop'
            : 'mobile';

        /* sku、handle 从 /cart/add 返回数据中实时获取 */
        const sku = data.sku
          || (Array.isArray(data.items) && data.items[0] && data.items[0].sku)
          || '';
        const handle = data.handle
          || (Array.isArray(data.items) && data.items[0] && data.items[0].handle)
          || '';
        const productTitle = data.product_title
          || (Array.isArray(data.items) && data.items[0] && data.items[0].product_title)
          || '';

        const params = {
          event_category: 'Product',
          event_label: variantId ? `variant_${variantId}` : 'add_to_cart',
          variant_id: variantId,
          quantity,
          section_id: cfg.sectionId || '',
          page_location: window.location.href,
          device_type: deviceType,
          transport_type: 'beacon'
        };
        if (cfg.productId != null) {
          params.product_id = String(cfg.productId);
        }
        if (sku) {
          params.sku = sku;
        }
        if (handle) {
          params.product_handle = handle;
        }
        if (productTitle) {
          params.product_title = productTitle;
        }

        const eventName = `${cfg.prefix}_add_to_cart_click`;
        if (typeof window.gtag === 'function') {
          window.gtag('event', eventName, params);
        }
      } catch (e) {
        /* 埋点失败不得影响加购 */
      }
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }

        /** 加购 UI 与事件分发完成后再发 GA，且异步避免 gtag 异常中断 try */
        const formDataSnapshot = formData;
        const dataSnapshot = data;
        queueMicrotask(() => {
          this.trackPdpGaAddToCartSuccess(dataSnapshot, formDataSnapshot);
        });
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
